import {
  getEFAProductForTimeSlot,
  getSPProductForTimeSlot,
  UK_TIMEZONE,
} from '@see-power-pft-common-js/utils/product-mapper';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Highcharts from 'highcharts';
import { ChartSeries } from 'pages/Availability/types';

export const getDateTickInterval = (
  data: ChartSeries[] | null,
): number | undefined => {
  // Default to Highcharts' auto interval if no data
  if (!data?.length) {
    return undefined;
  }

  const allXValues = data.flatMap(
    (series) =>
      series.data
        ?.map((point) => {
          if (point === null || typeof point !== 'object') return null;
          if (Array.isArray(point)) return point[0];
          return (point as Highcharts.PointOptionsObject).x;
        })
        .filter((x): x is number => typeof x === 'number') ?? [],
  );

  if (allXValues.length <= 1) {
    return undefined;
  }

  const minDate = Math.min(...allXValues);
  const maxDate = Math.max(...allXValues);
  const range = maxDate - minDate;
  const oneDayInMs = 24 * 3600 * 1000;

  if (range <= oneDayInMs) {
    return 3600 * 1000; // 1 hour
  }
  if (range <= 2 * oneDayInMs) {
    return 4 * 3600 * 1000; // 4 hours
  }
  if (range <= 7 * oneDayInMs) {
    return 12 * 3600 * 1000; // 12 hours
  }
  if (range <= 30 * oneDayInMs) {
    return 24 * 3600 * 1000; // 1 day
  }

  // Let Highcharts decide for larger ranges
  return undefined;
};

const getSeriesLabel = (
  seriesId: string | undefined,
  isPower: boolean,
): string => {
  if (!isPower) return 'energy limit';

  if (seriesId?.includes('export')) return 'export limit';
  if (seriesId?.includes('import')) return 'import limit';
  return 'limit';
};

// Helper function to filter points based on preview mode
const filterTooltipPoints = (
  points: Highcharts.TooltipFormatterContextObject['points'],
): Highcharts.Point[] => {
  if (!points) return [];

  const hasPreviewSeries = points.some((p) =>
    p.series.userOptions.id?.startsWith('new-'),
  );

  return points
    .map((tooltipPoint) => tooltipPoint.point)
    .filter((point) => {
      const seriesId = point.series.userOptions.id;
      const isValidPoint = point.y !== null;

      return hasPreviewSeries
        ? seriesId?.startsWith('new-') && isValidPoint
        : !seriesId?.startsWith('new-') && isValidPoint;
    });
};

export const tooltipFormatter = function (
  this: Highcharts.TooltipFormatterContextObject,
  isPower: boolean,
): string {
  if (this.x === undefined || typeof this.x !== 'number')
    throw new Error(
      'Invalid date format. Expected epoch time in milliseconds.',
    );

  const epochTime = this.x;

  const date = toZonedTime(epochTime, UK_TIMEZONE);
  const efa = getEFAProductForTimeSlot(epochTime);
  const sp = getSPProductForTimeSlot(epochTime);
  const title = `${format(date, 'd MMM yyyy')}, ${efa.toUpperCase()}, ${sp.toUpperCase()}, at ${format(date, 'HH:mm')}`;

  const filteredPoints = filterTooltipPoints(this.points);
  const pointsContent = filteredPoints
    .map((point) => {
      const value = Math.abs(point.y ?? 0).toFixed(2);
      const unit = isPower ? 'MW' : 'MWh';
      const label = getSeriesLabel(point.series.userOptions.id, isPower);

      return `<br/><b>${value} ${unit}</b> ${label}`;
    })
    .join('');

  return `<b>${title}</b>${pointsContent}`;
};

export const xAxisLabelsFormatter = function (
  this: Highcharts.AxisLabelsFormatterContextObject,
) {
  const date = toZonedTime(this.value, UK_TIMEZONE);

  // When the time is '00:00' in UK timezone, show the date in bold.
  if (date.getHours() === 0 && date.getMinutes() === 0) {
    return `<b>${format(date, 'd MMM')}</b>`;
  }
  // For all other ticks, show the time.
  return format(date, 'HH:mm');
};

export const yAxisLabelsFormatter = function (isPower: boolean) {
  return function (this: Highcharts.AxisLabelsFormatterContextObject) {
    return `${Math.abs(Number(this.value))}MW${!isPower ? 'h' : ''}`;
  };
};
